import { FC, useState } from "react"
import { Fixture } from "../../../models/fixture"
import { Team } from "../../../models/team"
import HelperService from "../../../services/helper"
import leagueService from "../../../services/league"
import useNotyf from "../../../context/useNotify"
import { UpdateFixturesDTO } from "../../../models/league"
import { useLeague } from "../../../context/useLeague"
import FixtureCard from "./FixtureCard"
import styled from "styled-components"
import { CreateStatsDTO } from "../../../models/stats"
import { useUser } from "../../../context/useUser"

interface Props {
  fixture: Fixture
  team: Team
  refresh: () => void
}

const SubmitStatsForm: FC<Props> = ({ fixture, team, refresh }) => {
  const notyf = useNotyf()
  const { league, getLeagueById } = useLeague()
  const { user } = useUser()

  // Data
  const [goals, setGoals] = useState<number | null>(null)
  const [assists, setAssists] = useState<number | null>(null)
  const [passes, setPasses] = useState<number | null>(null)
  const [tackles, setTackles] = useState<number | null>(null)
  const [saves, setSaves] = useState<number | null>(null)
  const [passesAttempted, setPassesAttempted] = useState<number | null>(null)
  const [passesCompleted, setPassesCompleted] = useState<number | null>(null)
  const [tacklesAttempted, setTacklesAttempted] = useState<number | null>(null)
  const [tacklesCompleted, setTacklesCompleted] = useState<number | null>(null)
  const [matchRating, setMatchRating] = useState<number | null>(null)
  const [shots, setShots] = useState<number | null>(null)

  // API
  const createStats = async () => {
    try {
      if (
          goals === null ||
          assists === null ||
          passes === null ||
          tackles === null ||
          saves === null ||
          passesAttempted === null ||
          passesCompleted === null ||
          tacklesAttempted === null ||
          tacklesCompleted === null ||
          matchRating === null ||
          shots === null ||
          !user ||
          !team ||
          !fixture ||
          !league
      ) {
        notyf.error("Please fill out all fields.")
        return
      }
      const dto: CreateStatsDTO = {
        fixtureId: fixture._id,
        teamId: team._id,
        userId: user._id,
        goals: goals,
        assists: assists,
        passes: passes,
        tackles: tackles,
        interceptions: 0,
        headers: 0,
        saves: saves,
        passesAttempted: passesAttempted,
        passesCompleted: passesCompleted,
        tacklesAttempted: tacklesAttempted,
        tacklesCompleted: tacklesCompleted,
        matchRating: matchRating,
        shots: shots,
      }
      await leagueService.createStats(league._id, dto)
      notyf.success("Match stats submitted.")
      handleRefreshData()
      refresh()
    } catch (err) {
      console.log("Error submitting stats: ", err)
      HelperService.showNotfyErr(notyf, err, "Error submitting stats.")
    }
  }

  // Methods
  const handleRefreshData = () => {
    if (!league) {
      return
    }
    getLeagueById(league?._id)
  }

  const handleChange =
    (setter: React.Dispatch<React.SetStateAction<number | null>>) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value ? parseInt(e.target.value, 10) : null
      setter(value)
    }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    // Validate and submit stats
    createStats()
  }

  return (
    <FormContainer
        onSubmit={handleSubmit}
    >
      <FixtureCardWrapper>
        <FixtureCard fixture={fixture} team={team} />
        <Overlay />
      </FixtureCardWrapper>

    <ScrollableContent>
      <div className="form-group">
        {createInputField("Goals", goals, handleChange(setGoals))}
        {createInputField("Assists", assists, handleChange(setAssists))}
        {createInputField("Passes", passes, handleChange(setPasses))}
        {createInputField("Tackles", tackles, handleChange(setTackles))}
        {createInputField("Saves", saves, handleChange(setSaves))}
        {createInputField("Passes Attempted", passesAttempted, handleChange(setPassesAttempted))}
        {createInputField("Passes Completed", passesCompleted, handleChange(setPassesCompleted))}
        {createInputField("Tackles Attempted", tacklesAttempted, handleChange(setTacklesAttempted))}
        {createInputField("Tackles Completed", tacklesCompleted, handleChange(setTacklesCompleted))}
        {createInputField("Match Rating", matchRating, handleChange(setMatchRating))}
        {createInputField("Shots", shots, handleChange(setShots))}
      </div>
    </ScrollableContent>

      <SubmitButton type="submit" className="btn btn-primary">
        Submit
      </SubmitButton>
    </FormContainer>
  )
}

export default SubmitStatsForm

const FormContainer = styled.form`
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 1rem;
`

const FixtureCardWrapper = styled.div`
  position: relative;
  margin-bottom: 1rem;
  flex-shrink: 0;
`

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 0.5rem;
  margin-bottom: 1rem;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(95, 168, 230, 0.5);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(95, 168, 230, 0.8);
  }
`

const SubmitButton = styled.button`
  flex-shrink: 0;
  margin-top: auto;
`

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
`

const createInputField = (
  label: string,
  stateValue: number | null,
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void
) => (
  <div className="form-group row mb-2">
    <label className="col-4 col-form-label text-right">{label}:</label>
    <div className="col-8">
      <input
        type="number"
        className="form-control bg-dark text-white"
        value={stateValue !== null ? stateValue : ""}
        onChange={handleChange}
      />
    </div>
  </div>
)
